import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  green: '#a7ff83',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
  accent: '#4ade80',
};

const PROXY = API_BASE;

function PriorityEngine({ navigate }) {
  const [open, setOpen] = useState(false);
  const [orderCount, setOrderCount] = useState(null);
  const [oldestBin, setOldestBin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPriority() {
      try {
        // Fetch open orders (BrickLink)
        const res = await fetch(`${PROXY}/api/orders`);
        const data = await res.json();
        const orders = Array.isArray(data) ? data : (data.data ?? []);
        const EXCLUDED = ['SHIPPED', 'RECEIVED', 'COMPLETED', 'CANCELLED', 'PURGED'];
        const blOpen = orders.filter(o => !EXCLUDED.includes(o.status));

        // Fetch open orders (BrickOwl)
        let boOpenCount = 0;
        try {
          const boRes = await fetch(`${PROXY}/api/brickowl/orders`);
          const boData = await boRes.json();
          const boOrders = Array.isArray(boData) ? boData : (boData.data ?? boData ?? []);
          const BO_EXCLUDED = ['Shipped', 'Received', 'Completed', 'Cancelled'];
          boOpenCount = boOrders.filter(o => !BO_EXCLUDED.includes(o.status)).length;
        } catch (e) { /* ignore BO errors */ }

        setOrderCount(blOpen.length + boOpenCount);

        // Fetch oldest unaudited bin
        const { data: auditRows } = await supabase
          .from('bin_audit_dates')
          .select('bin_name, last_audited')
          .order('last_audited', { ascending: true })
          .limit(1);

        if (auditRows && auditRows.length > 0) {
          const row = auditRows[0];
          const days = Math.floor(
            (Date.now() - new Date(row.last_audited).getTime()) / (1000 * 60 * 60 * 24)
          );
          setOldestBin({ name: row.bin_name, days });
        }

        // Auto-expand if anything urgent
        if (blOpen.length + boOpenCount > 0) setOpen(true);
      } catch (err) {
        console.error('Priority Engine fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPriority();
  }, []);

  const hasItems = orderCount > 0 || oldestBin;

  return (
    <div style={{
      marginBottom: '20px',
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Collapsible Header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: "'Outfit', sans-serif",
          borderBottom: open ? `1px solid ${colors.border}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: colors.accent, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            ◈ Priority
          </span>
          {!loading && orderCount > 0 && (
            <span style={{
              background: '#dc2626',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '20px',
            }}>
              {orderCount} order{orderCount !== 1 ? 's' : ''} pending
            </span>
          )}
          {!loading && orderCount === 0 && (
            <span style={{
              background: '#f0fdf4',
              color: colors.greenDark,
              fontSize: '0.65rem',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '20px',
            }}>
              All clear
            </span>
          )}
        </div>
        <span style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded Content */}
      {open && (
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Orders Row */}
          {loading ? (
            <div style={{ color: colors.textSecondary, fontSize: '0.82rem' }}>Loading priorities...</div>
          ) : orderCount > 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#fff5f5',
              border: '1px solid #fecaca',
              borderLeft: '4px solid #dc2626',
              borderRadius: '6px',
              padding: '12px 16px',
            }}>
              <div>
                <div style={{ fontWeight: 700, color: '#b91c1c', fontSize: '0.9rem' }}>
                  {orderCount} unpicked order{orderCount !== 1 ? 's' : ''}
                </div>
                <div style={{ color: colors.textSecondary, fontSize: '0.75rem', marginTop: '2px' }}>
                  Customers are waiting
                </div>
              </div>
              <button
                onClick={() => navigate('/orders')}
                style={{
                  padding: '8px 18px',
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                Go Pick →
              </button>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderLeft: '4px solid #4ade80',
              borderRadius: '6px',
              padding: '12px 16px',
              color: colors.greenDark,
              fontSize: '0.82rem',
              fontWeight: 600,
            }}>
              ✓ No open orders right now
            </div>
          )}

          {/* Oldest Bin Row */}
          {oldestBin && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderLeft: '4px solid #d97706',
              borderRadius: '6px',
              padding: '12px 16px',
            }}>
              <div>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
                  Bin {oldestBin.name} — {oldestBin.days} days ago
                </div>
                <div style={{ color: colors.textSecondary, fontSize: '0.75rem', marginTop: '2px' }}>
                  Longest unaudited bin
                </div>
              </div>
              <button
                onClick={() => navigate('/audit')}
                style={{
                  padding: '8px 18px',
                  background: '#d97706',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                Audit →
              </button>
            </div>
          )}

          {/* Quick Audit nudge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8faff',
            border: `1px solid ${colors.border}`,
            borderLeft: `4px solid ${colors.navyLight}`,
            borderRadius: '6px',
            padding: '12px 16px',
          }}>
            <div>
              <div style={{ fontWeight: 700, color: colors.navy, fontSize: '0.9rem' }}>
                Got 10 minutes?
              </div>
              <div style={{ color: colors.textSecondary, fontSize: '0.75rem', marginTop: '2px' }}>
                Quick Audit — knock out a few small bins
              </div>
            </div>
            <button
              onClick={() => navigate('/audit')}
              style={{
                padding: '8px 18px',
                background: colors.navy,
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: 'pointer',
              }}
            >
              Quick Audit →
            </button>
          </div>

        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [loaded, setLoaded] = useState(true);
  const [lookupText, setLookupText] = useState('');
  const navigate = useNavigate();
  const [lotCount, setLotCount] = useState('—');
  const [binCount, setBinCount] = useState('—');
  const [auditPct, setAuditPct] = useState('—');

  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await fetch(`${PROXY}/api/inventories`);
        const json = await res.json();
        const items = json.data || [];
        const bins = new Set(items.map(l => l.remarks).filter(Boolean));
        setLotCount(items.length.toLocaleString());
        setBinCount(bins.size);

        const { data: auditRows } = await supabase
          .from('bin_audit_dates')
          .select('bin_name');
        const auditedCount = auditRows?.length || 0;
        const pct = bins.size > 0 ? Math.round((auditedCount / bins.size) * 100) : 0;
        setAuditPct(pct);
      } catch (e) { console.error(e); }
    }
    fetchStats();
  }, []);

  const tiles = [
    { title: 'Orders', sub: 'Pick & pack', icon: '☰', path: '/orders' },
    { title: 'Stockroom', sub: 'Manage bins', icon: '▦', path: '/stockroom' },
    { title: 'Add', sub: 'Upload · Part Out · Quick', icon: '+', path: '/add' },
    { title: 'Finances', sub: 'Income & expenses', icon: '$', path: '/finances' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '28px',
        padding: '0 4px',
      }}>
        <div>
          <div style={{
            fontSize: '1.8rem',
            fontWeight: 800,
            color: colors.navy,
          }}>
            brick<span style={{ fontWeight: 400, color: colors.greenDark }}>audit</span>
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: colors.textSecondary,
          }}>
            {lotCount} lots · {binCount} bins · {auditPct}% audited
          </div>
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.navy,
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Quick Lookup */}
      <input
        type="text"
        placeholder="⌕  Quick lookup — part #, name, color, bin..."
        value={lookupText}
        onChange={e => setLookupText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            navigate(`/lookup?q=${encodeURIComponent(lookupText)}`);
          }
        }}
        style={{
          width: '100%', padding: '12px 16px',
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', color: colors.textPrimary,
          fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem',
          outline: 'none', boxSizing: 'border-box', marginBottom: '16px',
        }}
      />

      {/* Priority Engine */}
      <PriorityEngine navigate={navigate} />

      {/* Audit Mode Banner */}
      <div
        onClick={() => navigate('/audit')}
        style={{
          background: colors.navy,
          borderRadius: '8px',
          padding: '22px 24px',
          marginBottom: '20px',
          cursor: 'pointer',
          transition: 'box-shadow 0.15s ease',
          borderLeft: `4px solid ${colors.green}`,
        }}
        onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(7, 26, 82, 0.3)'}
        onMouseOut={(e) => e.currentTarget.style.boxShadow = 'none'}
      >
        <div style={{
          fontSize: '0.68rem',
          fontWeight: 700,
          color: colors.green,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: '6px',
        }}>✓ Start Auditing</div>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 800,
          color: '#ffffff',
          marginBottom: '4px',
        }}>Audit Mode</div>
        <div style={{
          fontSize: '0.82rem',
          color: '#94a3b8',
          lineHeight: 1.5,
        }}>Walk bin-by-bin through your stockroom. Every brick accounted for.</div>
      </div>

      {/* Page Tiles */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '10px',
      }}>
        {tiles.map((tile, i) => (
          <div key={i} style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '20px 14px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'transform 0.12s ease, box-shadow 0.12s ease',
          }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              e.currentTarget.style.borderColor = colors.navyLight;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = colors.border;
            }}
            onClick={() => navigate(tile.path)}
          >
            <div style={{
              fontSize: '1.6rem',
              marginBottom: '8px',
              color: colors.navy,
            }}>{tile.icon}</div>
            <div style={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: colors.textPrimary,
              marginBottom: '4px',
            }}>{tile.title}</div>
            <div style={{
              fontSize: '0.72rem',
              color: colors.textSecondary,
            }}>{tile.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Dashboard;